'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface IndustryModulesProps {
  currentLanguage: string;
}

const IndustryModules = ({ currentLanguage }: IndustryModulesProps) => {
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);

  const industries = [
    {
      id: 'power',
      name: 'Power Sector',
      icon: 'BoltIcon',
      color: 'warning',
      description: 'Low emission tracing via AI/IoT for power generation and distribution',
      capabilities: [
        'Real-time generation monitoring',
        'Renewable energy integration tracking',
        'Grid efficiency optimization',
        'Smart meter data analysis',
        'Emission factor calculation',
        'Predictive maintenance alerts',
      ],
      metrics: [
        { label: 'Generation Capacity Monitored', value: '2.5 GW' },
        { label: 'Renewable Mix', value: '42%' },
        { label: 'Emission Reduction', value: '18%' },
        { label: 'Grid Efficiency', value: '94%' },
      ],
    },
    {
      id: 'oil-gas',
      name: 'Oil & Gas',
      icon: 'FireIcon',
      color: 'error',
      description: 'Upstream and downstream emission monitoring with methane leak detection',
      capabilities: [
        'Flaring and venting monitoring',
        'Methane leak detection (AI/IoT)',
        'Refinery process tracking',
        'Transportation emissions',
        'Carbon intensity benchmarking',
        'Regulatory compliance reporting',
      ],
      metrics: [
        { label: 'Facilities Monitored', value: '47' },
        { label: 'Methane Reduction', value: '25%' },
        { label: 'Flaring Intensity', value: '-15%' },
        { label: 'Carbon Intensity', value: '3.2 tCO₂/bbl' },
      ],
    },
    {
      id: 'sustainable-power',
      name: 'Sustainable Power',
      icon: 'SunIcon',
      color: 'success',
      description: 'Maintenance and surveillance for renewable energy installations',
      capabilities: [
        'Solar/wind farm monitoring',
        'Performance degradation tracking',
        'Predictive maintenance',
        'Weather impact analysis',
        'Energy storage optimization',
        'ROI calculation',
      ],
      metrics: [
        { label: 'Renewable Assets', value: '850+' },
        { label: 'Uptime', value: '98.5%' },
        { label: 'Energy Yield', value: '+8%' },
        { label: 'Maintenance Cost', value: '-22%' },
      ],
    },
    {
      id: 'water',
      name: 'Water Conservation',
      icon: 'BeakerIcon',
      color: 'info',
      description: 'Water usage tracking and conservation program management',
      capabilities: [
        'Real-time flow monitoring',
        'Leak detection system',
        'Usage pattern analysis',
        'Treatment efficiency tracking',
        'Recycling metrics',
        'Conservation goal monitoring',
      ],
      metrics: [
        { label: 'Water Saved', value: '1.2M m³' },
        { label: 'Leak Reduction', value: '35%' },
        { label: 'Recycling Rate', value: '62%' },
        { label: 'Cost Savings', value: '$450K' },
      ],
    },
  ];

  const selectedData = industries.find((i) => i.id === selectedIndustry) || industries[0];

  return (
    <section id="industry-modules" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Industry-Specific Modules
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Specialized emission tracking solutions for Power, Oil & Gas, and Water sectors
          </p>
        </div>

        {/* Industry Selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {industries.map((industry) => (
            <button
              key={industry.id}
              onClick={() => setSelectedIndustry(industry.id)}
              className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                selectedIndustry === industry.id || (!selectedIndustry && industry.id === 'power')
                  ? `border-${industry.color} bg-${industry.color}/10 shadow-natural-lg`
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className={`inline-flex p-3 bg-${industry.color}/10 rounded-lg mb-4`}>
                <Icon name={industry.icon} size={32} className={`text-${industry.color}`} />
              </div>
              <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{industry.name}</h3>
              <p className="text-sm text-text-secondary">{industry.description}</p>
            </button>
          ))}
        </div>

        {/* Industry Details */}
        <div className="bg-card border-2 border-primary rounded-2xl p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Capabilities */}
            <div>
              <h3 className="font-heading font-bold text-xl text-text-primary mb-6">
                Key Capabilities
              </h3>
              <div className="space-y-3">
                {selectedData.capabilities.map((capability, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-surface rounded-lg p-4">
                    <Icon name="CheckCircleIcon" size={20} className={`text-${selectedData.color} mt-0.5 flex-shrink-0`} />
                    <span className="text-sm text-text-primary">{capability}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Metrics */}
            <div>
              <h3 className="font-heading font-bold text-xl text-text-primary mb-6">
                Performance Metrics
              </h3>
              <div className="space-y-4">
                {selectedData.metrics.map((metric, index) => (
                  <div key={index} className="bg-surface rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">{metric.label}</span>
                      <span className={`text-2xl font-heading font-bold text-${selectedData.color}`}>
                        {metric.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-gradient-to-r from-primary/5 to-secondary/5 border border-primary/20 rounded-xl p-5">
                <div className="flex items-start space-x-3">
                  <Icon name="LightBulbIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-primary mb-1 text-sm">Industry Insight</h4>
                    <p className="text-xs text-text-primary">
                      AI-powered analytics have identified optimization opportunities specific to {selectedData.name} operations, 
                      potentially reducing emissions by an additional 12-15% through targeted interventions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 pt-8 border-t border-border">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-text-primary mb-1">
                  Ready to implement {selectedData.name} solutions?
                </h4>
                <p className="text-sm text-text-secondary">
                  Our experts can customize the platform for your specific requirements
                </p>
              </div>
              <Link
                href="/get-started"
                className="whitespace-nowrap px-8 py-3 bg-conversion text-conversion-foreground font-heading font-semibold rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300"
              >
                Schedule Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustryModules;